import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertTriangle, UserPlus, Mail, RefreshCw } from 'lucide-react';

export const Register = () => {
  const { register, resendVerification } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await register(form.name, form.email, form.password, form.company);
      if (result.requires_verification) {
        setPendingEmail(result.email || form.email);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      await resendVerification(pendingEmail);
      setResent(true);
    } catch {
      // silently fail
    } finally {
      setResending(false);
    }
  };

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  if (pendingEmail) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
        <Card className="w-full max-w-md" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(16,185,129,0.15)' }}>
              <Mail className="w-7 h-7 text-emerald-400" />
            </div>
            <CardTitle className="text-2xl text-white" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }} data-testid="verify-heading">
              Verify Your Email
            </CardTitle>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed" data-testid="verify-desc">
              We've sent a verification link to <span className="text-white font-medium">{pendingEmail}</span>. Please check your inbox and click the link to activate your account.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-sm text-gray-500">
              Didn't receive it? Check your spam folder or{' '}
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-emerald-400 hover:underline inline-flex items-center gap-1"
                data-testid="resend-verification-btn"
              >
                {resending ? <><RefreshCw className="w-3 h-3 animate-spin" /> resending...</> : 'resend verification email'}
              </button>
            </div>
            {resent && (
              <p className="text-center text-xs text-emerald-400" data-testid="resend-success">
                Verification email resent!
              </p>
            )}
            <div className="pt-2 text-center">
              <Link to="/auth/login" className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="go-to-login">
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
      <Card className="w-full max-w-md" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardHeader className="text-center">
          <div className="text-xs tracking-[0.3em] text-emerald-500 font-semibold mb-2">DURABLE DESIGNS</div>
          <CardTitle className="text-2xl text-white" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Create Free Account
          </CardTitle>
          <p className="text-sm text-gray-400 mt-1">Create a Durable Designs account — access to Identity Testing Checker and approved beta tools.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded" data-testid="register-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">Full Name</Label>
              <Input id="name" value={form.name} onChange={set('name')} required
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" placeholder="John Smith" data-testid="register-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set('email')} required
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" placeholder="you@company.com" data-testid="register-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company" className="text-gray-300">Company (optional)</Label>
              <Input id="company" value={form.company} onChange={set('company')}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" placeholder="Acme Construction Ltd" data-testid="register-company" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set('password')} required minLength={6}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" placeholder="Min 6 characters" data-testid="register-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="register-submit">
              {loading ? 'Creating account...' : <><UserPlus className="w-4 h-4 mr-2" /> Create Account</>}
            </Button>
            <p className="text-center text-sm text-gray-400">
              Already have an account?{' '}
              <Link to="/auth/login" className="text-emerald-400 hover:underline" data-testid="login-link">Sign in</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
